import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    CpmButton,
    CpmCard,
    CpmCheckbox,
    CpmFilterDropdown,
    CpmPageShell,
} from '../../../components/design-system';
import {
    createTemplate,
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    getTemplateDetails,
    listCatalog,
    listStates,
    listTemplateTags,
    listTemplates,
} from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ActiveStatus,
    CatalogListRow,
    FormPhase,
    TemplateDetails,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { canTurnOn, copyTemplateName, stateName } from '../helpers';
import AppliedFilterChips from '../components/AppliedFilterChips';
import FilterToggleChip from '../components/FilterToggleChip';
import FilteredEmpty from '../components/FilteredEmpty';
import InfoI from '../components/InfoI';
import InPlaceTagToggle from '../components/InPlaceTagToggle';
import ReportTickList from '../components/ReportTickList';
import {
    ReportsAlert,
    ReportsFieldLabel,
    ReportsHelp,
    ReportsInput,
    ReportsSegmented,
    ReportsSkeletonList,
    ReportsWait,
    reportsGrid,
    span,
} from '../components/ReportsDialog';
import SearchField from '../components/SearchField';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export default function TemplateCreatePage() {
    const { id: duplicateId } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const isDuplicate = Boolean(duplicateId);

    const [initial, setInitial] = useState<TemplateDetails | null>(null);
    const [srcName, setSrcName] = useState('');
    const [loadPhase, setLoadPhase] = useState<FormPhase>(isDuplicate ? 'loading' : 'idle');
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        if (!duplicateId) return;
        let cancelled = false;
        setLoadPhase('loading');
        setLoadError('');
        getTemplateDetails(duplicateId)
            .then((details) => {
                if (cancelled) return;
                setSrcName(details.name);
                setInitial({ ...details, _id: '', name: copyTemplateName(details.name) });
                setLoadPhase('idle');
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(getReportsAdminErrorMessage(err));
                setLoadPhase('fetchError');
            });
        return () => {
            cancelled = true;
        };
    }, [duplicateId]);

    const goBack = () => navigate('/reports/templates');

    if (isDuplicate && loadPhase === 'loading') {
        return (
            <CpmPageShell gap={20} maxWidth={1360} variant="compactBottom">
                <ReportsWait label="Loading the template to duplicate…" gap={10} />
            </CpmPageShell>
        );
    }

    if (isDuplicate && loadPhase === 'fetchError') {
        return (
            <CpmPageShell gap={20} maxWidth={1360} variant="compactBottom">
                <ReportsAlert
                    tone="danger"
                    title="Couldn't load this template"
                    body="Neither the template's fields nor the reports in it came back, so this form is showing nothing rather than guessing. This says nothing about how many reports the template holds. The template list behind this is unaffected, and Catalogue and Store brands were not part of this request."
                    reason={reportsAdminErrorReason(loadError)}
                    action={
                        <CpmButton
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (!duplicateId) return;
                                setLoadPhase('loading');
                                setLoadError('');
                                getTemplateDetails(duplicateId)
                                    .then((details) => {
                                        setSrcName(details.name);
                                        setInitial({
                                            ...details,
                                            _id: '',
                                            name: copyTemplateName(details.name),
                                        });
                                        setLoadPhase('idle');
                                    })
                                    .catch((err) => {
                                        setLoadError(getReportsAdminErrorMessage(err));
                                        setLoadPhase('fetchError');
                                    });
                            }}
                        >
                            {REPORTS_COPY.RETRY_THIS_TEMPLATE}
                        </CpmButton>
                    }
                />
            </CpmPageShell>
        );
    }

    return (
        <TemplateCreateForm
            key={duplicateId || 'new'}
            initial={initial}
            isDuplicate={isDuplicate}
            srcName={srcName}
            onBack={goBack}
            onSaved={(row) => navigate(`/reports/templates?edit=${row._id}`)}
        />
    );
}

interface TemplateCreateFormProps {
    initial: TemplateDetails | null;
    isDuplicate: boolean;
    srcName: string;
    onBack: () => void;
    onSaved: (row: TemplateDetails) => void;
}

function StepPill({
    n,
    label,
    here,
    onClick,
}: {
    n: number;
    label: string;
    here: boolean;
    onClick?: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            className="flex items-center bg-white"
            style={{
                gap: 7,
                padding: '5px 11px 5px 7px',
                border: `1.5px solid ${here ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                borderRadius: 'var(--cpm-radius-full)',
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            <span
                className="inline-flex items-center justify-center"
                style={{
                    width: 18,
                    height: 18,
                    borderRadius: 'var(--cpm-radius-full)',
                    background: 'var(--cpm-purple-tint-13)',
                    fontSize: 10,
                    fontWeight: 800,
                    color: 'var(--cpm-brand-navy)',
                }}
            >
                {n}
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--cpm-text-primary)' }}>
                {label}
            </span>
            {here ? (
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: 'var(--cpm-radius-full)',
                        background: 'var(--cpm-brand-purple)',
                    }}
                />
            ) : null}
        </button>
    );
}

function TemplateCreateForm({
    initial,
    isDuplicate,
    srcName,
    onBack,
    onSaved,
}: TemplateCreateFormProps) {
    const [step, setStep] = useState<1 | 2>(1);
    const [name, setName] = useState(initial?.name ?? '');
    const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
    const [allStates, setAllStates] = useState(initial?.allStates ?? true);
    const [states, setStates] = useState<string[]>(initial?.states ?? []);
    const [activeStatus, setActiveStatus] = useState<ActiveStatus>(
        initial?.activeStatus ?? 'Active'
    );
    const [reportIds, setReportIds] = useState<string[]>(initial?.reportIds ?? []);
    const [tickRows, setTickRows] = useState<CatalogListRow[]>([]);
    const [tickPhase, setTickPhase] = useState<FormPhase>('idle');
    const [ticksLoaded, setTicksLoaded] = useState(false);
    const [tickSearch, setTickSearch] = useState('');
    const debouncedSearch = useDebouncedValue(tickSearch, 300);
    const [alreadyInOnly, setAlreadyInOnly] = useState(false);
    const [tickStates, setTickStates] = useState<string[]>(
        initial && !initial.allStates ? (initial.states ?? []) : []
    );
    const [tickTouched, setTickTouched] = useState(false);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);
    const [statesError, setStatesError] = useState(false);
    const [tagsError, setTagsError] = useState(false);
    const [takenNames, setTakenNames] = useState<string[]>([]);
    const [phase, setPhase] = useState<FormPhase>('idle');
    const [saveError, setSaveError] = useState('');
    const [justSaved, setJustSaved] = useState(false);
    const [savedRow, setSavedRow] = useState<TemplateDetails | null>(null);

    const loadOptions = () => {
        setStatesError(false);
        setTagsError(false);
        void listStates()
            .then(setStateDocs)
            .catch(() => setStatesError(true));
        void listTemplateTags()
            .then(setTagOptions)
            .catch(() => setTagsError(true));
        void listTemplates({ limit: 200 })
            .then((data) => setTakenNames((data.templates ?? []).map((row) => row.name)))
            .catch(() => undefined);
    };

    useEffect(() => {
        loadOptions();
    }, []);

    const loadTicks = async () => {
        setTickPhase('loading');
        try {
            const data = await listCatalog({ limit: 500 });
            setTickRows((data.reports ?? []).filter((row) => row.kind !== 'ALWAYS_ON'));
            setTicksLoaded(true);
            setTickPhase('idle');
        } catch {
            setTickPhase('fetchError');
        }
    };

    useEffect(() => {
        if (step === 2 && !ticksLoaded && tickPhase !== 'loading' && tickPhase !== 'fetchError') {
            void loadTicks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const goStep2 = () => {
        if (!name.trim() || nameTaken) return;
        if (!tickTouched && !allStates && states.length) {
            setTickStates(states);
        }
        setStep(2);
    };

    const trimmed = name.trim();
    const nameTaken = Boolean(
        trimmed && takenNames.some((item) => item.trim().toLowerCase() === trimmed.toLowerCase())
    );
    const block = !trimmed
        ? {
              title: 'A template needs a name',
              body: 'The name is what ops scans in the Templates list, so nothing is written without one. Tags, states and status are all optional.',
          }
        : nameTaken
          ? {
                title: 'A template is already called that',
                body: `Two templates must not be indistinguishable in the list. “${trimmed}” already exists — change this name before saving.`,
            }
          : null;

    const persist = async () => {
        if (block) {
            setStep(1);
            return;
        }
        setPhase('saving');
        setSaveError('');
        try {
            const row = await createTemplate({
                name: trimmed,
                tags,
                states: allStates ? [] : states,
                allStates,
                activeStatus,
                reportIds,
            });
            setJustSaved(true);
            setSavedRow(row);
            setPhase('idle');
        } catch (err) {
            setSaveError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const flight = phase === 'saving';
    const identityLine = `${trimmed || 'No name yet'} · ${
        allStates ? REPORTS_COPY.ALL_STATES : states.length ? states.join(', ') : 'No state yet'
    } · ${tags.length ? tags.join(', ') : 'No tags'} · ${activeStatus}`;

    const searching = tickSearch !== debouncedSearch;
    const filteredRows = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        return tickRows.filter((row) => {
            if (
                q &&
                !row.name.toLowerCase().includes(q) &&
                !(row.description ?? '').toLowerCase().includes(q)
            ) {
                return false;
            }
            if (
                tickStates.length &&
                !(row.allStates || tickStates.some((state) => (row.states ?? []).includes(state)))
            ) {
                return false;
            }
            if (alreadyInOnly && !reportIds.includes(row.reportID)) return false;
            return true;
        });
    }, [alreadyInOnly, debouncedSearch, reportIds, tickRows, tickStates]);

    const hasTickFilters =
        Boolean(debouncedSearch.trim()) || alreadyInOnly || tickStates.length > 0;
    const defaultNote =
        !tickTouched && !allStates && states.length
            ? `This template is tagged for ${states.join(' and ')}, so the list opened filtered to reports for ${
                  states.length === 1 ? 'that state' : 'those states'
              } plus all-states reports. Clear or change it if ops needs the rest of the catalogue.`
            : '';
    const countText = `${
        hasTickFilters
            ? `${filteredRows.length} of ${tickRows.length} reports match`
            : `${tickRows.length} reports in the catalogue`
    } · ${reportIds.length} ticked into this template`;

    const stateOptions = stateDocs
        .map(stateName)
        .filter(Boolean)
        .map((item) => ({ value: item, label: item }));

    const saveLabel = isDuplicate ? 'Save the duplicate' : 'Save the new template';

    return (
        <CpmPageShell gap={14} maxWidth={1360} variant="compactBottom">
            <div
                className="flex items-start justify-between flex-wrap"
                style={{ gap: 20, marginBottom: 8 }}
            >
                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: 9.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        {isDuplicate ? 'DUPLICATE TEMPLATE' : 'NEW TEMPLATE'}
                    </div>
                    <h1
                        className="m-0"
                        style={{
                            marginTop: 5,
                            fontSize: 30,
                            fontWeight: 800,
                            letterSpacing: '-0.025em',
                            color: 'var(--cpm-text-primary)',
                        }}
                    >
                        {isDuplicate ? `Duplicate “${srcName}”` : 'Create a template'}
                    </h1>
                    <div
                        style={{
                            marginTop: 8,
                            maxWidth: 700,
                            fontSize: 12.5,
                            fontWeight: 600,
                            lineHeight: 1.5,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {isDuplicate
                            ? 'A filled-in copy: same tags, states, status and the same reports ticked. Change the name on step 1 if ops needs to tell them apart. Nothing is written until you Save, and the template you copied is untouched.'
                            : 'Two steps: this template, then which reports are in it. One Save at the end writes both. No store brand changes — applying this template is a separate job on the saved template.'}
                    </div>
                </div>
                <CpmButton variant="ghost" size="sm" disabled={flight} onClick={onBack}>
                    {REPORTS_COPY.BACK_TO_TEMPLATES}
                </CpmButton>
            </div>

            <CpmCard padding={0}>
                <div style={{ padding: '20px 20px 26px' }}>
                    <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                        <StepPill
                            n={1}
                            label="This template"
                            here={step === 1}
                            onClick={() => {
                                if (!flight) setStep(1);
                            }}
                        />
                        <span
                            style={{
                                width: 14,
                                height: 1.5,
                                background: 'var(--cpm-border-strong)',
                            }}
                        />
                        <StepPill n={2} label="Which reports" here={step === 2} />
                        <span
                            style={{
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            {step === 1
                                ? 'Continue writes nothing.'
                                : justSaved
                                  ? 'Saved. Tick or untick and Save again.'
                                  : 'Tick what goes in, then Save once.'}
                        </span>
                    </div>

                    {justSaved ? (
                        <div style={{ marginTop: 16 }}>
                            <ReportsAlert
                                tone="success"
                                title="Saved"
                                body={`Written together in one save: the name, tags, states, status and the ${reportIds.length} report${
                                    reportIds.length === 1 ? '' : 's'
                                } ticked into this template. It is now a row in the Templates list. No store brand changed — applying this template to store brands is the separate job on the saved template.`}
                            />
                        </div>
                    ) : null}
                    {saveError ? (
                        <div style={{ marginTop: 16 }}>
                            <ReportsAlert
                                tone="danger"
                                title="Couldn't create this template"
                                body="Nothing was written — not the name, not the tags, not the ticks. Everything you set is still on this screen. No store brand was touched either way."
                                reason={reportsAdminErrorReason(
                                    saveError,
                                    'Nothing was written — not the name, not the tags, not the ticks.'
                                )}
                                action={
                                    <CpmButton
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void persist()}
                                    >
                                        Retry the save
                                    </CpmButton>
                                }
                            />
                        </div>
                    ) : null}

                    {step === 1 ? (
                        <div style={{ marginTop: 18 }}>
                            {block && trimmed ? (
                                <div style={{ marginBottom: 16 }}>
                                    <ReportsAlert
                                        tone="danger"
                                        title={block.title}
                                        body={block.body}
                                    />
                                </div>
                            ) : null}
                            <div style={reportsGrid}>
                                <div style={span(8)}>
                                    <ReportsFieldLabel required>Template name</ReportsFieldLabel>
                                    <ReportsInput
                                        value={name}
                                        disabled={flight}
                                        placeholder="What ops calls this template"
                                        onChange={(e) => setName(e.target.value)}
                                        style={
                                            nameTaken
                                                ? { borderColor: 'var(--cpm-error)' }
                                                : undefined
                                        }
                                    />
                                    <ReportsHelp>
                                        {nameTaken
                                            ? block?.title
                                            : 'Ops finds a template by this name, so make it say what the template is for.'}
                                    </ReportsHelp>
                                </div>
                                <div style={span(4)}>
                                    <ReportsFieldLabel>Template status</ReportsFieldLabel>
                                    <ReportsSegmented
                                        value={activeStatus}
                                        disabled={flight}
                                        options={[
                                            { value: 'Active', label: 'Active' },
                                            { value: 'Inactive', label: 'Inactive' },
                                        ]}
                                        onChange={setActiveStatus}
                                    />
                                </div>
                                <div style={span(6)}>
                                    <ReportsFieldLabel hint="Free text, more than one allowed">
                                        Tags
                                    </ReportsFieldLabel>
                                    <InPlaceTagToggle
                                        options={tagOptions}
                                        selected={tags}
                                        allowCreate
                                        disabled={flight}
                                        onChange={setTags}
                                    />
                                    {tagsError ? (
                                        <div style={{ marginTop: 8 }}>
                                            <div
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: 800,
                                                    color: 'var(--cpm-shortfall-text)',
                                                }}
                                            >
                                                {REPORTS_COPY.LOAD_FAIL_TAGS}
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                {REPORTS_COPY.OPTIONS_FAIL_BODY}
                                            </div>
                                            <div style={{ marginTop: 10 }}>
                                                <CpmButton
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={loadOptions}
                                                >
                                                    Retry
                                                </CpmButton>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                <div style={span(6)}>
                                    <CpmFilterDropdown
                                        label="States"
                                        portal
                                        disabled={flight}
                                        menuMinWidth={286}
                                        failed={statesError}
                                        failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                                        failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                        onRetry={loadOptions}
                                        options={stateOptions}
                                        selected={allStates ? [] : states}
                                        triggerLabel={
                                            allStates ? REPORTS_COPY.ALL_STATES : undefined
                                        }
                                        onChange={(next) => {
                                            setStates(next);
                                            if (next.length > 0) setAllStates(false);
                                        }}
                                    />
                                    <div style={{ marginTop: 9 }}>
                                        <CpmCheckbox
                                            checked={allStates}
                                            disabled={flight}
                                            onChange={() =>
                                                setAllStates((v) => {
                                                    if (!v) setStates([]);
                                                    return !v;
                                                })
                                            }
                                            label="All states — any store brand can be given this template"
                                        />
                                    </div>
                                    <ReportsHelp>
                                        A state-tagged template is offered to store brands in that
                                        state. A state is never a category.
                                    </ReportsHelp>
                                </div>
                            </div>
                            <div
                                className="flex items-center justify-between flex-wrap"
                                style={{
                                    gap: 16,
                                    marginTop: 18,
                                    padding: '14px 16px',
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    background: 'var(--cpm-surface-subtle)',
                                }}
                            >
                                <div
                                    style={{
                                        minWidth: 0,
                                        flex: '1 1 320px',
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        lineHeight: 1.5,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    Continue does not write anything. There is one Save, at the end
                                    of step 2, and it writes the name, tags, states, status and
                                    which reports are in the template together. No row appears on
                                    the Templates list until then.
                                </div>
                                <CpmButton
                                    variant="navy"
                                    size="lg"
                                    style={{ height: 42 }}
                                    disabled={flight || Boolean(block)}
                                    onClick={goStep2}
                                >
                                    {REPORTS_COPY.CONTINUE}
                                </CpmButton>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col" style={{ gap: 12, marginTop: 18 }}>
                            <div
                                className="flex items-center justify-between flex-wrap"
                                style={{
                                    gap: 14,
                                    padding: '12px 16px',
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    background: 'var(--cpm-surface-subtle)',
                                }}
                            >
                                <div
                                    style={{
                                        minWidth: 0,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-primary)',
                                    }}
                                >
                                    {identityLine}
                                </div>
                                <CpmButton
                                    variant="ghost"
                                    size="sm"
                                    disabled={flight}
                                    onClick={() => setStep(1)}
                                >
                                    Change these on step 1
                                </CpmButton>
                            </div>

                            <div
                                style={{
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    background: '#fff',
                                    padding: '16px 18px',
                                }}
                            >
                                <div className="flex items-baseline flex-wrap" style={{ gap: 10 }}>
                                    <span
                                        className="uppercase"
                                        style={{
                                            fontSize: 9.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                            color: 'var(--cpm-text-tertiary)',
                                        }}
                                    >
                                        WHICH REPORTS
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 22,
                                            fontWeight: 800,
                                            letterSpacing: '-0.02em',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {reportIds.length}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        {reportIds.length === 1 ? 'report' : 'reports'} ticked
                                    </span>
                                    <InfoI
                                        title={REPORTS_TIPS.NOT_IN_TEMPLATE.title}
                                        body="Always-on reports are already available to every store brand. Adding them to a template does not change what merchants can open."
                                    />
                                </div>
                                <div
                                    style={{
                                        marginTop: 5,
                                        maxWidth: 600,
                                        fontSize: 12,
                                        fontWeight: 500,
                                        lineHeight: 1.5,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    Tick a report to put it in this template, untick to take it out.
                                    Ticking writes nothing on its own — the one Save writes these
                                    ticks and the details together, and no store brand changes
                                    either way. Zero reports is allowed.
                                </div>

                                {tickPhase === 'fetchError' ? (
                                    <div style={{ marginTop: 14 }}>
                                        <ReportsAlert
                                            tone="danger"
                                            title="Couldn't load the catalogue reports"
                                            body="The reports ops can tick did not come back, so this is not a catalogue with nothing in it. Nothing has been added or removed, and what you typed on this template is still here. The Templates list behind this is untouched."
                                            action={
                                                <CpmButton
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => void loadTicks()}
                                                >
                                                    Retry the catalogue
                                                </CpmButton>
                                            }
                                        />
                                    </div>
                                ) : tickPhase === 'loading' || !ticksLoaded ? (
                                    <div style={{ marginTop: 14 }}>
                                        <div style={{ paddingBottom: 14 }}>
                                            <ReportsWait
                                                gap={10}
                                                label="Getting the catalogue reports ops can tick into this template. Blank here is not a catalogue with nothing in it."
                                            />
                                        </div>
                                        <ReportsSkeletonList rows={4} />
                                    </div>
                                ) : tickRows.length === 0 ? (
                                    <div
                                        className="flex flex-col items-center text-center"
                                        style={{
                                            marginTop: 14,
                                            padding: '26px 22px',
                                            border: '1px solid var(--cpm-border-default)',
                                            borderRadius: 10,
                                        }}
                                    >
                                        <div
                                            className="inline-flex items-center justify-center"
                                            style={{
                                                width: 72,
                                                height: 72,
                                                borderRadius: 18,
                                                background: 'var(--cpm-open-bg)',
                                                color: 'var(--cpm-open-icon)',
                                                marginBottom: 16,
                                            }}
                                        >
                                            <svg
                                                width="32"
                                                height="32"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M4 6h16M4 12h16M4 18h10" />
                                            </svg>
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 18,
                                                fontWeight: 800,
                                                letterSpacing: '-0.02em',
                                            }}
                                        >
                                            No report exists in the catalogue yet
                                        </div>
                                        <div
                                            style={{
                                                maxWidth: 440,
                                                marginTop: 7,
                                                fontSize: 13,
                                                fontWeight: 500,
                                                lineHeight: 1.5,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            There is nothing to tick — this is not a search with no
                                            hits. Create a report in Catalogue and it appears here.
                                            A template with 0 reports is still allowed to be saved.
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ marginTop: 14 }}>
                                        <div
                                            className="flex items-center flex-wrap"
                                            style={{ gap: 9 }}
                                        >
                                            <SearchField
                                                value={tickSearch}
                                                onChange={setTickSearch}
                                                placeholder="Search a report by name"
                                                disabled={flight}
                                                width={272}
                                                size="sm"
                                            />
                                            <FilterToggleChip
                                                label={REPORTS_COPY.ALREADY_IN_TEMPLATE}
                                                on={alreadyInOnly}
                                                disabled={flight}
                                                onClick={() => {
                                                    setAlreadyInOnly((v) => !v);
                                                    setTickTouched(true);
                                                }}
                                            />
                                            <CpmFilterDropdown
                                                label="State"
                                                triggerVariant="chip"
                                                portal
                                                disabled={flight}
                                                menuMinWidth={286}
                                                failed={statesError}
                                                failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                                                failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                                onRetry={loadOptions}
                                                options={stateOptions}
                                                selected={tickStates}
                                                onChange={(next) => {
                                                    setTickStates(next);
                                                    setTickTouched(true);
                                                }}
                                            />
                                        </div>
                                        <AppliedFilterChips
                                            chips={[
                                                ...tickStates.map((state) => ({
                                                    key: `st-${state}`,
                                                    label: state,
                                                    onClear: () => {
                                                        setTickStates((prev) =>
                                                            prev.filter((item) => item !== state)
                                                        );
                                                        setTickTouched(true);
                                                    },
                                                })),
                                                ...(alreadyInOnly
                                                    ? [
                                                          {
                                                              key: 'already',
                                                              label: REPORTS_COPY.ALREADY_IN_TEMPLATE,
                                                              onClear: () => {
                                                                  setAlreadyInOnly(false);
                                                                  setTickTouched(true);
                                                              },
                                                          },
                                                      ]
                                                    : []),
                                                ...(debouncedSearch.trim()
                                                    ? [
                                                          {
                                                              key: 'q',
                                                              label: `Search: ${debouncedSearch.trim()}`,
                                                              onClear: () => setTickSearch(''),
                                                          },
                                                      ]
                                                    : []),
                                            ]}
                                            onClearAll={() => {
                                                setTickSearch('');
                                                setTickStates([]);
                                                setAlreadyInOnly(false);
                                                setTickTouched(true);
                                            }}
                                        />
                                        {defaultNote ? (
                                            <div
                                                style={{
                                                    marginTop: 10,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {defaultNote}
                                            </div>
                                        ) : null}
                                        <div
                                            style={{
                                                margin: '14px 0 10px',
                                                fontSize: 12.5,
                                                fontWeight: 800,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {countText}
                                        </div>
                                        {searching ? (
                                            <div>
                                                <div style={{ paddingBottom: 12 }}>
                                                    <ReportsWait
                                                        gap={10}
                                                        label="Searching the catalogue. The list is not ready yet — blank here is not “the catalogue is empty”."
                                                    />
                                                </div>
                                                <ReportsSkeletonList rows={3} />
                                            </div>
                                        ) : filteredRows.length === 0 && hasTickFilters ? (
                                            <FilteredEmpty
                                                title="No report matches what you have on"
                                                cause="The search and filters caused this — the catalogue is not empty, and this is not “this template holds 0 reports”."
                                                restated={`Still on: ${[
                                                    alreadyInOnly
                                                        ? REPORTS_COPY.ALREADY_IN_TEMPLATE
                                                        : '',
                                                    ...tickStates,
                                                    debouncedSearch.trim()
                                                        ? `Search: ${debouncedSearch.trim()}`
                                                        : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}.`}
                                                totalInPlace={`${tickRows.length} reports exist`}
                                                onClear={() => {
                                                    setTickSearch('');
                                                    setTickStates([]);
                                                    setAlreadyInOnly(false);
                                                    setTickTouched(true);
                                                }}
                                            />
                                        ) : (
                                            <ReportTickList
                                                rows={filteredRows}
                                                selectedIds={reportIds}
                                                layout="create"
                                                disabled={flight}
                                                onToggle={(reportID) => {
                                                    const row = tickRows.find(
                                                        (item) => item.reportID === reportID
                                                    );
                                                    const on = reportIds.includes(reportID);
                                                    if (!on && row && !canTurnOn(row)) return;
                                                    setReportIds((prev) =>
                                                        on
                                                            ? prev.filter(
                                                                  (item) => item !== reportID
                                                              )
                                                            : [...prev, reportID]
                                                    );
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            {justSaved && savedRow ? (
                                <div
                                    className="flex items-start justify-between flex-wrap"
                                    style={{
                                        gap: 16,
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                        background: '#fff',
                                        padding: '16px 18px',
                                    }}
                                >
                                    <div style={{ minWidth: 0, maxWidth: 620 }}>
                                        <div
                                            className="uppercase"
                                            style={{
                                                fontSize: 9.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.08em',
                                                color: 'var(--cpm-text-tertiary)',
                                            }}
                                        >
                                            APPLY THIS TEMPLATE TO STORE BRANDS
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 6,
                                                fontSize: 13,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            Available now that the template is saved
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 4,
                                                fontSize: 12,
                                                fontWeight: 500,
                                                lineHeight: 1.5,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            Apply lives on the saved template in Templates. It
                                            writes only what each store brand is missing, never
                                            changes which reports are in the template, and no store
                                            brand is linked to it afterwards.
                                        </div>
                                    </div>
                                    <CpmButton
                                        variant="outline"
                                        size="lg"
                                        style={{ height: 42 }}
                                        onClick={() => onSaved(savedRow)}
                                    >
                                        Open this template in Templates
                                    </CpmButton>
                                </div>
                            ) : null}

                            <div
                                className="flex items-center justify-between flex-wrap"
                                style={{
                                    gap: 16,
                                    padding: '14px 16px',
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    background: 'var(--cpm-surface-subtle)',
                                }}
                            >
                                <div style={{ minWidth: 0, flex: '1 1 280px' }}>
                                    {flight ? (
                                        <ReportsWait
                                            gap={10}
                                            label="Creating this template — the details and the ticks in one write. It has not finished, so do not fire it again."
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                lineHeight: 1.5,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            One Save writes the name, tags, states, status and which
                                            reports are in this template, together. It changes no
                                            store brand.
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                                    <CpmButton
                                        variant="outline"
                                        size="lg"
                                        style={{ height: 42 }}
                                        disabled={flight}
                                        onClick={() => setStep(1)}
                                    >
                                        Back to step 1
                                    </CpmButton>
                                    <CpmButton
                                        variant="navy"
                                        size="lg"
                                        style={{ height: 42 }}
                                        disabled={flight || Boolean(block)}
                                        isLoading={flight}
                                        onClick={() => void persist()}
                                    >
                                        {saveLabel}
                                    </CpmButton>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </CpmCard>
        </CpmPageShell>
    );
}
